/**
 * Utility functions for processing game data for video conversion
 */

/**
 * Filters and formats game data for the video conversion API
 * @param {Object} gameData - The original game data
 * @returns {Object} - Filtered and formatted game data
 */
export const filterGameData = (gameData) => {
  if (!gameData) return null;
  
  // Create a copy of the game data
  const filteredData = {...gameData};
  
  // Remove specified fields
  const fieldsToRemove = [
    'comments',
    'createdAt',
    'creator',
    'creatorHandle',
    'pov',
    'score',
    'starredMoves',
    'suggestedMoves',
    'updatedAt'
  ];
  
  fieldsToRemove.forEach(field => {
    if (filteredData.hasOwnProperty(field)) {
      delete filteredData[field];
    }
  });
  
  // Rename caption to videoTitle
  if (filteredData.caption !== undefined) {
    filteredData.videoTitle = filteredData.caption;
    delete filteredData.caption;
  }
  
  // Extract starting position information
  const startPosition = {
    fen: filteredData.fen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1', // Default starting position
    arrows: [],
    highlights: []
  };
  
  // Format moves into PGN format while extracting visual elements
  if (filteredData.moves) {
    try {
      // Parse the moves array if it's a string
      let movesArray = filteredData.moves;
      if (typeof movesArray === 'string') {
        try {
          movesArray = JSON.parse(movesArray);
        } catch (e) {
          console.error("Error parsing moves string:", e);
          movesArray = [];
        }
      }
      
      // Initialize PGN string and arrows & squares array
      let pgnString = '';
      let moveNumber = 1;
      let isWhiteMove = true;
      const arrowsAndSquares = [];
      
      // Process each move
      if (Array.isArray(movesArray)) {
        for (let i = 0; i < movesArray.length; i++) {
          const move = movesArray[i];
          
          // Extract visual elements for this move
          const moveVisuals = {
            moveIndex: i,
            arrows: move.arrows || [],
            highlights: move.highlights || move.highlightedSquares || []
          };
          
          // Add to arrows & squares array
          arrowsAndSquares.push(moveVisuals);
          
          // Add move number at the start of white's move
          if (isWhiteMove) {
            pgnString += moveNumber + '. ';
          }
          
          // Add the move notation
          if (move.notation) {
            pgnString += move.notation + ' ';
          }
          
          // Add content in curly braces if it exists
          if (move.content && move.content.trim() !== '') {
            pgnString += '{' + move.content + '} ';
          }
          
          // Update move tracking
          if (!isWhiteMove) {
            moveNumber++;
          }
          isWhiteMove = !isWhiteMove;
        }
      }
      
      // Add the PGN string
      filteredData.pgn = pgnString.trim();
      
      // Add structured data in a more readable format
      filteredData.startPosition = startPosition;
      filteredData.arrowsAndSquares = arrowsAndSquares;
      
      // Keep original moves for reference but remove the duplicate 'moves'
      filteredData.originalMoves = movesArray;
      delete filteredData.moves;
      
    } catch (error) {
      console.error("Error formatting moves to PGN:", error);
      // Keep the original moves if there's an error
      filteredData.originalMoves = filteredData.moves;
      delete filteredData.moves;
    }
  }
  
  
  
  return filteredData;
};

/**
 * Prepares game data for sending to the video conversion API
 * @param {Object} gameData - The original game data
 * @returns {Object} - API-ready game data
 */
export const prepareGameForVideoConversion = (gameData) => {
  const filteredData = filterGameData(gameData);
  
  // Additional processing could be added here in the future
  
  return filteredData;
}; 